import React from "react";
import { Link } from "react-router-dom";
import productData from "../Datas/productData";
import siteConfig, { formatPrice } from "../config/siteConfig";
import "../css/main.scss";

const getProductImage = (product, index = 0) =>
  product?.images?.[index] || product?.images?.[0] || "";

const getProductSummary = (product) => {
  if (product?.shortDescription) return product.shortDescription;
  if (product?.attributes?.cut) return product.attributes.cut;
  if (product?.tags?.length) return product.tags.slice(0, 2).join(" · ");
  return product?.category || siteConfig.tagline;
};

const ProductBadge = ({ product }) => {
  const previousPrice = product?.compareAtPrice ?? product?.originalPrice;
  const isSale = Number(previousPrice) > Number(product?.price);
  const label = isSale ? "SALE" : product?.newArrival ? "NEW" : "";

  return label ? (
    <span className={`main-product__badge${isSale ? " is-sale" : ""}`}>
      {label}
    </span>
  ) : null;
};

function Main() {
  const coreCuts = [1, 2, 3, 4, 5, 6]
    .map((id) => productData.find((product) => Number(product.id) === id))
    .filter(Boolean);
  const featuredProducts = productData
    .filter((product) => product.featured)
    .concat(productData.filter((product) => !product.featured))
    .filter(
      (product, index, products) =>
        products.findIndex((item) => item.id === product.id) === index
    )
    .slice(0, 8);

  const scrollToTop = () => {
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  return (
    <div className="main">
      <main>
        <section className="main-hero" aria-labelledby="main-hero-title">
          <img
            className="main-hero__image"
            src="/img/editorial-hero.webp"
            alt={`${siteConfig.brandName} fall and winter editorial`}
          />
          <div className="main-hero__overlay" />
          <div className="main-hero__content">
            <p className="main-hero__eyebrow">{siteConfig.wordmark}</p>
            <h1 id="main-hero-title">FW26</h1>
            <p>{siteConfig.tagline}</p>
            <div className="main-hero__actions">
              <Link to="/shop" onClick={scrollToTop}>
                Collection
              </Link>
              <Link to="/about" onClick={scrollToTop}>
                Lookbook
              </Link>
            </div>
          </div>
        </section>

        <section className="main-editorials" aria-label="Latest stories">
          <Link
            className="main-editorial"
            to="/shop"
            onClick={scrollToTop}
          >
            <img
              src="/img/editorial-reloved.webp"
              alt={`${siteConfig.brandName} reloved clothing`}
            />
            <span className="main-editorial__overlay" />
            <span className="main-editorial__content">
              <span className="main-editorial__eyebrow">Built to live on</span>
              <strong>Reloved Clothing</strong>
              <span className="main-editorial__link">Shop now</span>
            </span>
          </Link>

          <Link
            className="main-editorial"
            to="/shop?sort=newest"
            onClick={scrollToTop}
          >
            <img
              src="/img/editorial-future.webp"
              alt={`${siteConfig.brandName} future releases`}
            />
            <span className="main-editorial__overlay" />
            <span className="main-editorial__content">
              <span className="main-editorial__eyebrow">Coming next</span>
              <strong>Future Releases</strong>
              <span className="main-editorial__link">Discover</span>
            </span>
          </Link>
        </section>

        <section className="main-core" aria-labelledby="core-cuts-title">
          <header className="main-section-intro">
            <p className="main-section-intro__eyebrow">
              {siteConfig.brandName} denim
            </p>
            <h2 id="core-cuts-title">Our Core Cuts</h2>
            <p>{siteConfig.description}</p>
          </header>

          <div className="main-core__rail">
            {coreCuts.map((product) => (
              <article className="main-core-card" key={product.id}>
                <Link
                  to={`/shopDetails/${product.id}`}
                  onClick={scrollToTop}
                  aria-label={`View ${product.name}`}
                >
                  <span className="main-core-card__media">
                    <img
                      src={getProductImage(product)}
                      alt={product.name}
                      loading="lazy"
                    />
                  </span>
                  <span className="main-core-card__body">
                    <strong>{product.name}</strong>
                    <span>{getProductSummary(product)}</span>
                    <span className="main-core-card__link">
                      View the cut <span aria-hidden="true">→</span>
                    </span>
                  </span>
                </Link>
              </article>
            ))}
          </div>
        </section>

        <section className="main-education" aria-labelledby="what-is-title">
          <img
            src="/img/editorial-selvedge.webp"
            alt={`${siteConfig.brandName} selvedge denim detail`}
            loading="lazy"
          />
          <div className="main-education__overlay" />
          <div className="main-education__content">
            <p>What is...</p>
            <h2 id="what-is-title">Selvedge Denim?</h2>
            <span>{siteConfig.tagline}</span>
            <Link to="/about" onClick={scrollToTop}>
              Discover the details
            </Link>
          </div>
        </section>

        <section
          className="main-featured page-shell"
          aria-labelledby="featured-title"
        >
          <header className="main-featured__header">
            <div>
              <p>{siteConfig.wordmark}</p>
              <h2 id="featured-title">Featured Goods</h2>
            </div>
            <Link to="/shop" onClick={scrollToTop}>
              View all <span aria-hidden="true">→</span>
            </Link>
          </header>

          <div className="main-featured__grid">
            {featuredProducts.map((product) => {
              const secondaryImage = product?.images?.[1] || "";

              return (
                <article
                  className={`main-product${secondaryImage ? " has-secondary" : ""}`}
                  key={product.id}
                >
                  <Link
                    to={`/shopDetails/${product.id}`}
                    onClick={scrollToTop}
                  >
                    <span className="main-product__media">
                      <img
                        className="main-product__image main-product__image--primary"
                        src={getProductImage(product)}
                        alt={product.name}
                        loading="lazy"
                      />
                      {secondaryImage && (
                        <img
                          className="main-product__image main-product__image--secondary"
                          src={secondaryImage}
                          alt=""
                          loading="lazy"
                        />
                      )}
                      <ProductBadge product={product} />
                    </span>
                    <span className="main-product__details">
                      <strong>{product.name}</strong>
                      <span>{formatPrice(product.price)}</span>
                    </span>
                  </Link>
                </article>
              );
            })}
          </div>
        </section>
      </main>
    </div>
  );
}

export default Main;
